/* eslint-disable react/prop-types */
import './index.scss';
import ModalBase from '.';
import { ModalFormActionProps } from '../../types';
import ModalExecuteAction from './ModalExecuteAction';
import 'react-datepicker/dist/react-datepicker.css';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { FaCalendarAlt } from 'react-icons/fa';
import { Alert, Input, Select } from '@piggytechng/component-library';
import { OverideInputStyle, OverideSelectStyle } from '../../helpers/utils';
import useTargetSavings from '../../hooks/useTargetSavings';
import useCustomers from '../../hooks/useCustomers';

function ModalAction({
  modalIsOpen,
  context,
  action,
  setIsOpen,
  close,
  title,
  actionColor,
  buttonText,
  data,
  targetId,
  userId,
}: ModalFormActionProps) {
  const [showExecuteAction, setShowExecuteAction] = useState(false);
  const { runTargetAction, isRunningTargetAction } = useTargetSavings();
  const { runUserAction, isRunningUserAction, flagUser, unflagUser, deleteUserBank, deleteUserCard } = useCustomers();
  const SelectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const frequencies = [
    {
      label: 'Daily',
      value: 'daily',
    },
    {
      label: 'Weekly',
      value: 'weekly',
    },
    {
      label: 'Monthly',
      value: 'monthly',
    },
    {
      label: 'Manual',
      value: 'manual',
    },
  ];

  const [targetFormData, setTargetFormData] = useState<any>({
    'update-start-date': {
      startDate: '',
    },
    'update-end-date': {
      stopDate: '',
    },
    'update-frequency': {
      frequency: '',
    },
  });

  const [userFormData, setUserFormData] = useState<any>({
    editName: {
      firstName: '',
      lastName: '',
    },
    deleteBvn: {
      value: '',
    },
    editEmailAddress: {
      value: '',
    },
    editPhoneNumber: {
      value: '',
    },
    deleteBank: {
      accountNumber: '',
      bankName: '',
    },
    flagUser: {
      reason: '',
    },
  });

  const userFormType = useMemo(() => {
    switch (action) {
      case 'editName':
        return 'name';
      case 'deleteBvn':
        return 'bvn';
      case 'editEmailAddress':
        return 'email';
      case 'editPhoneNumber':
        return 'phoneNo';
      case 'deleteBank':
        return 'delete-bank';
      case 'deleteCard':
        return 'delete-card';
      case 'flagUser':
        return 'flag';
      case 'unflagUser':
        return 'unflag';
      default:
        return '';
    }
  }, [action]);

  const resetTargetFormData = () => {
    setTargetFormData({
      'update-start-date': {
        startDate: '',
      },

      'update-end-date': {
        stopDate: '',
      },
      'update-frequency': {
        frequency: '',
      },
    });
    setUserFormData({
      editName: {
        firstName: '',
        lastName: '',
      },
      deleteBvn: {
        value: '',
      },
      editEmailAddress: {
        value: '',
      },
      editPhoneNumber: {
        value: '',
      },
      deleteBank: {
        accountNumber: '',
        bankName: '',
      },
      flagUser: {
        reason: '',
      },
    });
  };

  function destroyAction() {
    setShowExecuteAction(false);
    resetTargetFormData();
    close && close();
    setIsOpen && setIsOpen(false);
  }

  const handleIconClick = () => {
    const input = document.querySelector('.react-datepicker__input-container input');
    if (input) {
      (input as HTMLElement).click();
    }
  };

  const handleFormInputChange = (e: any, name: string) => {
    const { value } = e.target;

    if (context === 'target') {
      setTargetFormData((prev: any) => ({
        ...prev,
        [action as string]: {
          ...prev[action],
          [name]: value,
        },
      }));
    } else if (context === 'user') {
      setUserFormData((prev: any) => ({
        ...prev,
        [action as string]: {
          ...prev[action],
          [name]: value,
        },
      }));
    }
  };

  const isLoading = useMemo(() => {
    return isRunningTargetAction || isRunningUserAction;
  }, [isRunningTargetAction, isRunningUserAction]);

  const handleAction = async (password: string) => {
    if (context === 'target') {
      runTargetAction(targetId as string, { ...targetFormData[action], action, password }, destroyAction);
    } else if (context === 'user') {
      runUserAction(
        userId as string,
        {
          type: userFormType,
          ...(action === 'editName'
            ? {
                value: {
                  firstName: userFormData[action]?.firstName,
                  lastName: userFormData[action]?.lastName,
                },
              }
            : action === 'deleteBank'
              ? {
                  accountNumber: userFormData[action]?.accountNumber,
                  bankName: userFormData[action]?.bankName,
                }
              : action === 'flagUser'
                ? {
                    reason: userFormData[action]?.reason,
                  }
                : {
                    value: userFormData[action]?.value,
                  }),
        },
        destroyAction,
      );
    }
  };

  const handleCustomAction = async (password: string) => {
    if (action === 'flagUser') {
      flagUser(userId as string, { ...userFormData[action], password }, destroyAction);
    } else if (action === 'unflagUser') {
      unflagUser(userId as string, { ...userFormData[action], password }, destroyAction);
    } else if (action === 'deleteCard') {
      deleteUserCard(userId as string, { ...userFormData[action], password }, destroyAction);
    } else if (action === 'deleteBank') {
      deleteUserBank(userId as string, { ...userFormData[action], password }, destroyAction);
    } else {
      handleAction(password);
    }
  };

  const onButtonClick = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (action === 'editName' && context === 'user') {
      if (!userFormData[action]?.firstName || !userFormData[action]?.lastName) {
        Alert.error('Both first name and last name are required');
        return;
      }
    }

    if (
      action !== 'editName' &&
      action !== 'deleteBank' &&
      action !== 'flagUser' &&
      action !== 'unflagUser' &&
      context === 'user'
    ) {
      if (!userFormData[action]?.value) {
        Alert.error('This field is required');
        return;
      }
    }

    if (action === 'deleteBank' && context === 'user') {
      if (!userFormData[action]?.accountNumber || !userFormData[action]?.bankName) {
        Alert.error('Both bank name and account number are required');
        return;
      }
    }

    if (action === 'flagUser' && context === 'user') {
      if (!userFormData[action]?.reason) {
        Alert.error('Reason is required');
        return;
      }
    }

    setIsOpen && setIsOpen(false);
    setShowExecuteAction(true);
  };

  const mapValuesToForm = () => {
    if (context === 'user' && data && JSON.stringify(data) !== JSON.stringify(userFormData[action as string])) {
      setUserFormData((prev: any) => ({
        ...prev,
        [action as string]: {
          ...prev[action as string],
          ...data,
        },
      }));
    }
  };

  useEffect(() => {
    if (modalIsOpen) {
      mapValuesToForm();
    }
  }, [data, modalIsOpen]);

  return (
    <>
      <ModalBase
        modalIsOpen={modalIsOpen}
        setIsOpen={(state: boolean) => {
          setIsOpen && setIsOpen(state);

          if (!state) resetTargetFormData();
        }}
      >
        <div className={`modal-base general success action modal-action flex--4`}>
          <span onClick={destroyAction}>&times;</span>
          <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} action="">
            <h4>{title}</h4>

            {context === 'target' && (action === 'update-start-date' || action === 'update-end-date') && (
              <>
                <div className="input-cont flex--4">
                  <label htmlFor="startDate">Select Date</label>
                  <DatePicker
                    selected={
                      action === 'update-start-date'
                        ? targetFormData[action]?.startDate
                          ? new Date(targetFormData[action]?.startDate)
                          : null
                        : targetFormData[action]?.stopDate
                          ? new Date(targetFormData[action]?.stopDate)
                          : null
                    }
                    name={action === 'update-start-date' ? 'startDate' : 'stopDate'}
                    onChange={(date) =>
                      handleFormInputChange(
                        { target: { value: date ? date.toISOString().split('T')[0] : '' } },
                        action === 'update-start-date' ? 'startDate' : 'stopDate',
                      )
                    }
                    className="date-picker"
                    placeholderText={action === 'update-start-date' ? 'Select start date' : 'Select end date'}
                    dateFormat="dd MMMM, yyyy"
                  />
                  <FaCalendarAlt onClick={handleIconClick} className="calendar" color="#212337" />
                </div>
              </>
            )}

            {context === 'target' && action === 'update-frequency' && (
              <>
                <div className="input-cont flex--4">
                  <label htmlFor="frequency">Select Frequency</label>

                  <Select
                    {...SelectStyle}
                    options={frequencies}
                    onChange={(e) => handleFormInputChange({ target: { value: e } }, 'frequency')}
                    value={targetFormData[action]?.frequency}
                  />
                </div>
              </>
            )}

            {context === 'user' && action === 'editName' && (
              <>
                <Input
                  {...OverideInputStyle}
                  label="First Name"
                  id="firstName"
                  name="firstName"
                  className="user-form-input"
                  placeholder="Enter first name"
                  onChange={(e) => handleFormInputChange({ target: { value: e.target.value } }, 'firstName')}
                  value={userFormData[action]?.firstName}
                />
                <Input
                  {...OverideInputStyle}
                  label="Last Name"
                  id="lastName"
                  name="lastName"
                  placeholder="Enter last name"
                  onChange={(e) => handleFormInputChange(e, 'lastName')}
                  className="user-form-input"
                  value={userFormData[action]?.lastName}
                />
              </>
            )}

            {context === 'user' && action === 'deleteBvn' && (
              <div className="input-cont flex--2">
                <Input
                  {...OverideInputStyle}
                  label="BVN"
                  id="bvn"
                  name="bvn"
                  className="user-form-input"
                  placeholder="Enter BVN"
                  {...OverideInputStyle}
                  type="text"
                  height="60px"
                  onChange={(e) => {
                    e.preventDefault();
                    handleFormInputChange(e, 'value');
                  }}
                  value={userFormData[action]?.value}
                />
              </div>
            )}

            {context === 'user' && action === 'editEmailAddress' && (
              <Input
                {...OverideInputStyle}
                label="Email"
                name="value"
                className="user-form-input"
                placeholder="Enter email"
                onChange={(e) => {
                  e.preventDefault();
                  handleFormInputChange(e, 'value');
                }}
                value={userFormData[action]?.value}
              />
            )}

            {context === 'user' && action === 'editPhoneNumber' && (
              <Input
                {...OverideInputStyle}
                label="Phone Number"
                name="value"
                className="user-form-input"
                placeholder="Enter phone number"
                onChange={(e) => {
                  e.preventDefault();
                  handleFormInputChange(e, 'value');
                }}
                value={userFormData[action]?.value}
              />
            )}

            {context === 'user' && action === 'deleteBank' && (
              <>
                <Input
                  {...OverideInputStyle}
                  label="Bank Name"
                  id="bankName"
                  name="bankName"
                  className="user-form-input"
                  placeholder="Enter bank name"
                  onChange={(e) => handleFormInputChange(e, 'bankName')}
                  value={userFormData[action]?.bankName}
                />
                <Input
                  {...OverideInputStyle}
                  label="Account Number"
                  id="accountNumber"
                  name="accountNumber"
                  placeholder="Enter account number"
                  onChange={(e) => handleFormInputChange(e, 'accountNumber')}
                  className="user-form-input"
                  value={userFormData[action]?.accountNumber}
                />
              </>
            )}

            {context === 'user' && action === 'flagUser' && (
              <Input
                {...OverideInputStyle}
                label="Reason"
                id="reason"
                name="reason"
                className="user-form-input"
                placeholder="Enter reason for flagging"
                onChange={(e) => handleFormInputChange(e, 'reason')}
                value={userFormData[action]?.reason}
              />
            )}

            {context === 'user' && action === 'unflagUser' && <p>Are you sure you want to unflag this user?</p>}

            <div className="button-cont flex--2">
              <button
                onClick={() => {
                  destroyAction();
                }}
              >
                Cancel
              </button>
              <button onClick={onButtonClick} style={{ backgroundColor: actionColor }}>
                {buttonText}
              </button>
            </div>
          </form>
        </div>
      </ModalBase>

      <ModalExecuteAction
        modalIsOpen={showExecuteAction}
        setIsOpen={(state: boolean) => {
          setShowExecuteAction(state);
          if (!state) resetTargetFormData();
        }}
        isLoading={isLoading}
        actionColor={actionColor}
        text={title}
        action={(password: string) => handleCustomAction(password)}
      />
    </>
  );
}

export default ModalAction;
